import { ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/shared/ui/Button';

import { FormFields } from './FormFields';
import type { FormField } from './formModel';
import styles from './GroupInput.module.scss';

export interface GroupInputProps {
  field: FormField;
  value: unknown;
  /** Полный путь группы для ошибок: `faq` или `body.2.faq`. */
  errorKey: string;
  errors: Record<string, string>;
  siteID: string;
  onChange: (value: unknown) => void;
}

type Item = Record<string, unknown>;

/**
 * Повторяющаяся группа: пункты меню, вопросы FAQ, характеристики. Каждый
 * элемент — небольшая форма из полей группы.
 */
export function GroupInput({ field, value, errorKey, errors, siteID, onChange }: GroupInputProps) {
  const items = asItems(value);

  // Ключи элементов для разметки живут отдельно от значений: у элемента нет
  // своего идентификатора, а без стабильного ключа набранное прыгало бы при
  // перестановке соседей.
  const [ids, setIds] = useState<string[]>(() => items.map(() => crypto.randomUUID()));
  const keys =
    ids.length === items.length ? ids : items.map((_, index) => ids[index] ?? String(index));

  const max = field.rules.max_items;
  const full = max !== undefined && items.length >= max;

  const update = (next: Item[], nextIds: string[]) => {
    setIds(nextIds);
    onChange(next);
  };

  const move = (index: number, delta: number) => {
    const target = index + delta;

    if (target < 0 || target >= items.length) {
      return;
    }

    const next = [...items];
    const nextIds = [...keys];

    [next[index], next[target]] = [next[target] ?? {}, next[index] ?? {}];
    [nextIds[index], nextIds[target]] = [nextIds[target] ?? '', nextIds[index] ?? ''];
    update(next, nextIds);
  };

  return (
    <div className={styles['group']}>
      {items.length === 0 && <p className={styles['empty']}>Пока пусто.</p>}

      {items.map((item, index) => (
        <section key={keys[index]} className={styles['item']}>
          <header className={styles['item__head']}>
            <span className={styles['item__title']}>{itemTitle(field, item, index)}</span>

            <div className={styles['item__actions']}>
              <Button
                iconOnly
                size="sm"
                variant="ghost"
                tone="neutral"
                aria-label="Поднять"
                disabled={index === 0}
                onClick={() => {
                  move(index, -1);
                }}
              >
                <ChevronUp size={15} />
              </Button>
              <Button
                iconOnly
                size="sm"
                variant="ghost"
                tone="neutral"
                aria-label="Опустить"
                disabled={index === items.length - 1}
                onClick={() => {
                  move(index, 1);
                }}
              >
                <ChevronDown size={15} />
              </Button>
              <Button
                iconOnly
                size="sm"
                variant="ghost"
                tone="danger"
                aria-label="Удалить"
                onClick={() => {
                  update(
                    items.filter((_, i) => i !== index),
                    keys.filter((_, i) => i !== index),
                  );
                }}
              >
                <Trash2 size={15} />
              </Button>
            </div>
          </header>

          <FormFields
            fields={field.subfields}
            values={item}
            errors={errors}
            path={`${errorKey}.${String(index)}.`}
            siteID={siteID}
            onChange={(key, next) => {
              update(
                items.map((current, i) => (i === index ? { ...current, [key]: next } : current)),
                keys,
              );
            }}
          />
        </section>
      ))}

      <Button
        variant="outline"
        tone="neutral"
        size="sm"
        iconLeft={<Plus size={15} />}
        disabled={full}
        onClick={() => {
          update([...items, {}], [...keys, crypto.randomUUID()]);
        }}
      >
        {full ? `Не больше ${String(max)}` : 'Добавить'}
      </Button>
    </div>
  );
}

/** Заголовок элемента — первое заполненное текстовое поле, иначе номер. */
function itemTitle(field: FormField, item: Item, index: number): string {
  for (const sub of field.subfields) {
    const value = item[sub.key];

    if (
      (sub.type === 'string' || sub.type === 'text') &&
      typeof value === 'string' &&
      value.trim() !== ''
    ) {
      return value.trim();
    }
  }

  return `${field.name}: ${String(index + 1)}`;
}

function asItems(value: unknown): Item[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.map((item) => (typeof item === 'object' && item !== null ? (item as Item) : {}));
}
